<template>
  <BCard>
    <div class="text-black text-2xl font-semibold mb-2">
      Credential Management
    </div>

    <BCol class="p-0 d-flex">
      <div class="d-flex rounded-lg align-items-center border search-bar mr-2">
        <span class="k-search-normal-1 h-100 font-bold text-10 pt-[8px] align-middle mx-[7px]" />
        <BFormInput
          v-model="keyword"
          placeholder="Cari Nama"
          class="border-0"
        />
      </div>

      <BButton
        v-if="isAdmin"
        variant="link"
        class="button-all ml-auto px-1 d-flex align-items-center flex-shrink-0"
        @click="$emit('tambah')"
      >
        <img
          src="https://storage.googleapis.com/komerce/assets/komerce-icon/Putih/add.svg"
          alt="plus"
          class="w-5 h-5 mr-1"
        >
        <strong>Tambah</strong>
      </BButton>
    </BCol>

    <BOverlay
      id="scroll"
      :show="loading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <div
        id="table"
        ref="tableContainer"
        style="height: calc(100vh - 275px); overflow-y: auto;"
        class="mt-[24px]"
        @scroll="handleScroll"
      >
        <BTable
          :items="filteredItems"
          :fields="computedFields"
          empty-text="Tidak ada data yang ditampilkan."
          responsive
          show-empty
          thead-class="text-nowrap"
        >
          <template #cell(platform)="data">
            <div class="flex items-center">
              {{ data.item.platform }}
              <a
                :href="normalizeUrl(data.item.loginUrl)"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-1 text-secondary"
              >
                <img
                  src="https://storage.googleapis.com/komerce/assets/icons/new-tab-blue.svg"
                  alt="Open link"
                  class="w-4 h-4 cursor-pointer"
                >
              </a>
            </div>
          </template>

          <template #cell(email)="data">
            <div class="d-flex align-items-center min-w-0">
              {{ data.item.email }}
            </div>
          </template>

          <template #cell(password)="data">
            <div class="d-flex items-center min-w-0">
              <span
                class="truncate block"
              >{{ '•'.repeat(data.item.password? data.item.password.length : 8) }}</span>

              <div class="relative inline-flex items-center">
                <BSpinner
                  v-if="copyingId === data.item.id"
                  small
                  style="width: 14px; height: 14px;"
                  class="ml-1"
                />
                <img
                  v-else
                  src="https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/copy.svg"
                  class="ml-1 cursor-pointer w-4 h-4 shrink-0"
                  @click="copyPassword(data.item)"
                >
              </div>
            </div>
          </template>

          <template #cell(note)="data">
            <div
              v-if="data.item.note && data.item.note !== '-'"
              v-b-tooltip.hover.top
              class="max-w-[200px] truncate"
              :title="data.item.note"
            >
              {{ data.item.note }}
            </div>
            <div
              v-else
              class="text-center"
            >
              -
            </div>
          </template>

          <template #cell(action)="data">
            <div class="d-flex justify-content-start gap-6">

              <template v-if="isAdmin">
                <BButton
                  style="width: 32px; height: 32px; padding: 2px;"
                  class="d-flex align-items-center justify-content-center"
                  size="sm"
                  variant="flat-primary"
                  @click="$emit('assign', data.item)"
                >
                  <img
                    src="https://storage.googleapis.com/komerce/assets/icons/profile-2user.svg"
                    alt="Assign"
                    class="w-full h-full shrink-0"
                  >
                </BButton>
                <BButton
                  style="width: 32px; height: 32px; padding: 2px;"
                  class="d-flex align-items-center justify-content-center"
                  size="sm"
                  variant="flat-warning"
                  @click="$emit('edit', data.item)"
                >
                  <img
                    src="https://storage.googleapis.com/komerce/assets/icons/edit-yellow.svg"
                    alt="Edit"
                    class="w-full h-full shrink-0"
                  >
                </BButton>
                <BButton
                  style="width: 32px; height: 32px; padding: 2px;"
                  class="d-flex align-items-center justify-content-center"
                  size="sm"
                  variant="flat-danger"
                  @click="$emit('delete', data.item)"
                >
                  <img
                    src="https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/trash.svg"
                    alt="Hapus"
                    class="w-full h-full shrink-0"
                  >
                </BButton>
              </template>
            </div>
          </template>
        </BTable>
      </div>
    </BOverlay>
  </BCard>
</template>

<script>
import _ from 'lodash'
import { FIELDS_CREDENTIAL, credentialAxios, normalizeUrl } from './config'

export default {
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['tambah', 'assign', 'edit', 'delete', 'pin-expired', 'copy-password-request', 'load-more', 'copy-success', 'copy-error', 'search'],

  data() {
    return {
      keyword: '',
      copiedKey: null,
      copyingId: null,
      fields: FIELDS_CREDENTIAL,
    }
  },

  computed: {
    computedFields() {
      return this.fields.filter(
        f => this.isAdmin || f.key !== 'action',
      )
        .map(f => {
          if (f.key !== 'action') {
            return {
              ...f,
              tdClass: this.isAdmin ? 'py-1 align-middle' : 'py-2 align-middle',
            }
          }
          return f
        })
    },

    filteredItems() {
      return this.items
    },
  },

  watch: {
    keyword: _.debounce(function search(val) {
      if (val.length === 0 || val.length >= 3) {
        this.$emit('search', val)
      }
    }, 500),
  },

  methods: {
    normalizeUrl,
    handleScroll() {
      const table = this.$refs.tableContainer
      if (
        table.scrollTop >= table.scrollHeight - table.offsetHeight - 5
        && !this.loading
      ) {
        this.$emit('load-more')
      }
    },

    handleCopyError(err, item) {
      if (err.response?.status === 403) {
        const message = err.response.data?.meta?.message || ''
        if (message.toLowerCase().includes('pin')) {
          this.$emit('pin-expired', { onValid: () => this.doCopyPassword(item) })
        } else {
          this.$emit('copy-error', { response: { data: { meta: { message: 'Akses ditolak, kredensial ini tidak ditugaskan ke role Anda' } } } })
        }
      } else {
        this.$emit('copy-error', err)
      }
    },

    copyPassword(item) {
      this.$emit('copy-password-request', item)
    },

    async doCopyPassword(item) {
      this.copyingId = item.id
      try {
        const url = this.isAdmin
          ? `v1/credential_management/secure/admin/${item.id}/copy`
          : `v1/credential_management/secure/staff/${item.id}/copy`

        const res = await credentialAxios.post(
          url,
          {},
        )

        const { data, meta } = res.data

        if (meta.status !== 'success') {
          this.$emit('copy-error', { response: { data: { meta: { message: meta.message || 'Gagal menyalin password' } } } })
          return
        }

        const decoded = atob(data)
        await navigator.clipboard.writeText(decoded)
        this.$emit('copy-success')
      } catch (err) {
        this.handleCopyError(err, item)
      } finally {
        this.copyingId = null
      }
    },
  },
}
</script>

<style lang="scss">
@import './CredentialManagement.scss';
</style>
